import { Injectable } from "@angular/core";

/**
 * Base64 encode an ArrayBuffer in a browser-safe way
 */
export function base64Encode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000; // chunk size to avoid call stack overflow
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunk))
    );
  }
  return btoa(binary);
}

/**
 * Base64 decode into a Uint8Array in a browser-safe way
 */
export function base64Decode(b64: string): Uint8Array {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

@Injectable({ providedIn: 'root' })
export class KeyStoreService {
  private readonly PREFIX = 'key-';
  private readonly TAG = '🔑 KeyStore';

  /** Save a raw AES key for a conversation (32 bytes) */
  async save(raw: ArrayBuffer | Uint8Array, convId: string): Promise<void> {
    const buf = raw instanceof Uint8Array ? raw.buffer : raw;
    const b64 = base64Encode(buf);
    localStorage.setItem(`${this.PREFIX}${convId}`, b64);
    console.log(`${this.TAG}(localStorage): saved key for ${convId}`);
  }

  /** Load a raw AES key if it exists */
  async load(convId: string): Promise<ArrayBuffer | null> {
    const b64 = localStorage.getItem(`${this.PREFIX}${convId}`);
    if (!b64) {
      console.warn(`${this.TAG}: no key found for ${convId}`);
      return null;
    }
    console.log(`${this.TAG}(localStorage): loaded key for ${convId}`);
    return base64Decode(b64).buffer;
  }

  /** Delete stored key */
  delete(convId: string): void {
    localStorage.removeItem(`${this.PREFIX}${convId}`);
    console.log(`${this.TAG}(localStorage): deleted key for ${convId}`);
  }

  /** Find the first identity key (account prefix: x25519-) */
  findFirstIdentityKey(): { userId: string; data: ArrayBuffer } | null {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${this.PREFIX}x25519-`)) {
        const b64 = localStorage.getItem(key);
        if (!b64) continue;
        const raw = base64Decode(b64).buffer;
        const userId = key.replace(`${this.PREFIX}x25519-`, '');
        console.log(`${this.TAG}(localStorage): found identity key for ${userId}`);
        return { userId, data: raw };
      }
    }
    return null;
  }
}
